if (!customElements.get('slideshow-component')) {
  customElements.define(
    'slideshow-component',
    class SlideshowComponent extends HTMLElement {
      constructor() {
        super();

        this.sliderControls = this.querySelector('.swiper-controls');
        this.sliderInstance = false;
        this.enableSlider = this.dataset.enableSlider === 'true';
        this.selectedIndex = this.selectedIndex;
        this.sectionId = this.dataset.sectionId;
        this.section = this.closest(`.section-${this.sectionId}`);
        this.containerWidth = this.dataset.container;
        this.cleanupScrollAnimations = [];
        this.enableZoom = this.dataset.enableZoom ? this.dataset.enableZoom === 'true' : true;
        this.borderRadius = this.dataset.borderRadius;
        this.zoom = 0;

        if (!this.enableSlider) return;

        this.setupZoom();
        window.addEventListener('resize', this.setupZoom.bind(this));

        this.initSlider();
      }

      static get observedAttributes() {
        return ['selected-index'];
      }

      get selectedIndex() {
        return parseInt(this.getAttribute('selected-index')) || 0;
      }

      set selectedIndex(index) {
        this.setAttribute('selected-index', `${index}`);
      }

      setupZoom() {
        if (!this.enableZoom) return;

        this.destroyZoom();

        const pageWidthEl = this.querySelector('.page-width');
        const paddingLeft = window.getComputedStyle(pageWidthEl).getPropertyValue('padding-left');
        this.zoom = Math.min(parseFloat(paddingLeft.replace('px', '')), window.innerWidth * 0.04);

        if (this.zoom > 15) {
          this.initZoom();
        }
      }

      initSlider() {
        const additionModules = [FoxTheme.Swiper.Autoplay, FoxTheme.Swiper.EffectFade];

        this.sliderOptions = {
          slidesPerView: 1,
          spaceBetween: 10,
          loop: true,
          grabCursor: true,
          allowTouchMove: true,
          threshold: 2,
          effect: 'fade',
          on: {
            init: this.handleAfterInit.bind(this),
          },
          fadeEffect: {
            crossFade: true,
          },
        };

        if (this.sliderControls) {
          this.sliderOptions.navigation = {
            nextEl: this.sliderControls.querySelector('.swiper-button-next'),
            prevEl: this.sliderControls.querySelector('.swiper-button-prev'),
          };
          this.sliderOptions.pagination = {
            el: this.sliderControls.querySelector('.swiper-pagination'),
            clickable: true,
          };
        }

        const autoplayDelay = parseInt(this.dataset.autoplay);
        if (autoplayDelay > 0) {
          this.sliderOptions = {
            ...this.sliderOptions,
            autoplay: {
              delay: autoplayDelay,
              disableOnInteraction: false,
            },
          };
        }

        this.sliderInstance = new window.FoxTheme.Carousel(this, this.sliderOptions, additionModules);
        this.sliderInstance.init();

        this.sliderInstance.slider.on('realIndexChange', this.handleSlideChange.bind(this));
        this.sliderInstance.slider.on('slideChangeTransitionStart', this.handleSlideTransitionStart.bind(this));

        if (this.sliderInstance) {
          this.selectedElement = this.sliderInstance.slider.slides[this.sliderInstance.slider.activeIndex];
          this.onReady(this.selectedElement, this.sliderInstance.slider.slides);
          this.loadSlideMedia(this.selectedElement);
          this.preloadAdjacentSlides(this.sliderInstance.slider);

          // Fix accessibility
          const focusableElements = FoxTheme.a11y.getFocusableElements(this);
          focusableElements.forEach((element) => {
            if (!element.classList.contains('swiper-button') && !element.classList.contains('btn')) {
              // MAHA (2026-08-26): con loop:true Swiper clona cada slide para el
              // wrap-around (clase swiper-slide-duplicate, creada una sola vez
              // al iniciar -- confirmado en vivo). El clon repite cualquier
              // contenido focusable del slide real (incluida la capa clicable
              // de sections/slideshow.liquid) con el MISMO href: sin esto el
              // tabulador se detiene DOS veces en el mismo destino (confirmado
              // con Playwright: paradas #66 y #70 al mismo /collections/limpieza,
              // #67 y #71 al mismo /products/freidora-...). El clon sigue
              // siendo clicable con el mouse cuando esta activo (lo maneja
              // el propio CSS .swiper-fade de Swiper); esto solo lo saca del
              // tabulador y del arbol de accesibilidad -- el slide real de
              // ese mismo destino sigue siendo alcanzable con Tab.
              const duplicateSlide = element.closest('.swiper-slide-duplicate');
              if (duplicateSlide) {
                element.setAttribute('tabindex', '-1');
                element.setAttribute('aria-hidden', 'true');
                return;
              }

              element.addEventListener('focusin', () => {
                const slide = element.closest('.swiper-slide');
                this.sliderInstance.slider.slideToLoop(this.sliderInstance.slider.slides.indexOf(slide));
              });
            }
          });
        }
      }

      onReady(selectedElement) {
        if (selectedElement.dataset.type === 'video') {
          const videoElement = FoxTheme.utils.displayedMedia(selectedElement.querySelectorAll('video-element'));
          videoElement?.play();
        }

        if (!FoxTheme.config.motionReduced) {
          const motionEls = selectedElement.querySelectorAll('motion-element');
          motionEls.forEach((motionEl) => {
            setTimeout(() => {
              motionEl && motionEl.refreshAnimation();
            });
          });
        }
      }

      handleAfterInit() {
        this.removeAttribute('data-media-loading');

        // Fix active bullet not transition on the first time.
        if (this.sliderControls) {
          const activeBullet = this.sliderControls.querySelector('.swiper-pagination-bullet-active');

          if (activeBullet) {
            activeBullet.classList.remove('swiper-pagination-bullet-active');
            activeBullet.offsetHeight; // Trigger reflow.
            activeBullet.classList.add('swiper-pagination-bullet-active');
          }
        }
      }

      handleSlideChange(swiper) {
        const { slides, realIndex, activeIndex } = swiper;
        this.selectedIndex = realIndex;

        this.updateControlsScheme(slides[activeIndex]);
      }

      // MAHA (2026-08-25): carga diferida de imagenes del carrusel del hero. El
      // Liquid deja src/srcset en data-src/data-srcset para todo slide que no sea
      // el primero (o no tenga enable_preload_image); aqui se activan justo cuando
      // el slide esta por mostrarse. Se precargan el ANTERIOR y el SIGUIENTE (nunca
      // todos): medido en vivo, con loop:true un "anterior" desde el slide 1 salta
      // al ULTIMO slide, que con solo "siguiente" precargado nunca tenia ventaja y
      // mostraba un hueco real (304ms/111ms a velocidad normal). Se probo precargar
      // el anterior en tiempo ocioso (requestIdleCallback) para no sumar sus ~25KB
      // a la carga inicial, pero el hilo principal tiene huecos ociosos casi de
      // inmediato (no solo tras el evento load): medido en vivo, seguia sumando
      // los 3 slides/85KB al momento de 'load' igual que la version sincrona, sin
      // ganar nada -- se descarto por no cumplir el objetivo, no por error de
      // codigo. Verificado tambien que el hueco hacia ADELANTE bajo throttling
      // "Slow 3G" (hasta 8.9s en el primer paso) es un problema de congestion de
      // red del PESO TOTAL de la pagina, no de direccion de precarga: no lo
      // resuelve precargar mas slides, y no se persigue mas desde el carrusel.
      loadSlideMedia(slide) {
        if (!slide || slide.dataset.mediaLoaded === 'true') return;
        slide.dataset.mediaLoaded = 'true';

        const picture = slide.querySelector('picture');
        if (!picture) return;

        picture.querySelectorAll('source[data-srcset]').forEach((source) => {
          source.setAttribute('srcset', source.dataset.srcset);
          source.removeAttribute('data-srcset');
        });

        const img = picture.querySelector('img[data-src]');
        if (img) {
          if (img.dataset.srcset) {
            img.setAttribute('srcset', img.dataset.srcset);
            img.removeAttribute('data-srcset');
          }
          img.setAttribute('src', img.dataset.src);
          img.removeAttribute('data-src');
        }
      }

      // Con loop:true, swiper.slides[activeIndex +/- 1] puede caer en un CLON del
      // loop (mismo data-index que el real, pero un elemento de DOM distinto) en
      // vez del slide real vecino -- medido en vivo: precargaba el clon y dejaba
      // el slide real sin precarga, sin ganar nada. Se ubica el vecino real por
      // realIndex + data-index, ignorando los .swiper-slide-duplicate. swiper.slides
      // es una coleccion Dom7 (jQuery-like): su .find()/.filter() esperan un
      // SELECTOR CSS, no un callback -- hay que pasar por Array.from() primero
      // para usar los metodos nativos de Array.
      findRealSlide(swiper, dataIndex) {
        return Array.from(swiper.slides).find(
          (s) => !s.classList.contains('swiper-slide-duplicate') && s.getAttribute('data-index') === String(dataIndex)
        );
      }

      preloadAdjacentSlides(swiper) {
        if (!swiper || !swiper.slides || !swiper.slides.length) return;
        const total = Array.from(swiper.slides).filter((s) => !s.classList.contains('swiper-slide-duplicate')).length;
        if (!total) return;
        const nextIndex = (swiper.realIndex + 1) % total;
        const prevIndex = (swiper.realIndex - 1 + total) % total;
        this.loadSlideMedia(this.findRealSlide(swiper, nextIndex));
        this.loadSlideMedia(this.findRealSlide(swiper, prevIndex));
      }

      handleSlideTransitionStart(swiper) {
        this.loadSlideMedia(swiper.slides[swiper.activeIndex]);
        this.preloadAdjacentSlides(swiper);
      }

      attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
          const fromElements = this.querySelectorAll(`[data-swiper-slide-index="${oldValue}"]`);
          const toElements = this.querySelectorAll(`[data-swiper-slide-index="${newValue}"]`);

          fromElements.forEach((fromElement) => {
            if (fromElement.dataset.type === 'video') {
              const videoElement = FoxTheme.utils.displayedMedia([fromElement.querySelector('video-element')]);
              videoElement && videoElement.pause();
            }

            const motionEls = fromElement.querySelectorAll('motion-element');
            motionEls &&
              motionEls.forEach((motionEl) => {
                if (motionEl.hasAttribute('data-text')) {
                  motionEl.resetAnimation();
                }
              });
          });

          toElements.forEach((toElement) => {
            setTimeout(() => {
              if (toElement.classList.contains('swiper-slide-active')) {
                if (toElement.dataset.type === 'video') {
                  const videoElement = FoxTheme.utils.displayedMedia([toElement.querySelector('video-element')]);
                  videoElement && videoElement.play();
                }

                const motionEls = toElement.querySelectorAll('motion-element');
                motionEls.forEach((motionEl) => {
                  setTimeout(() => {
                    motionEl && motionEl.refreshAnimation();
                  });
                });
              }
            });
          });
        }
      }

      updateControlsScheme(activeSlide) {
        if (this.sliderControls) {
          const classesToRemove = Array.from(this.sliderControls.classList).filter((className) =>
            className.startsWith('color-')
          );
          classesToRemove.forEach((className) => this.sliderControls.classList.remove(className));
          const colorScheme = activeSlide.dataset.colorScheme;
          this.sliderControls.classList.add(colorScheme);
        }
      }

      initZoom() {
        if (this.containerWidth !== 'full') return;

        const motionTarget = {
          target: this.section,
          offset: [
            [-0.1, 0],
            [0.7, 0],
          ],
        };

        this.querySelectorAll('.slideshow__item-wrap').forEach((slide) => {
          const slideBg = slide.querySelector('.slideshow__bg');

          this.cleanupScrollAnimations.push(
            FoxTheme.Motion.scroll(
              FoxTheme.Motion.animate(slideBg, {
                transform: [`scale(1) translateZ(0)`, `scale(1.1) translateZ(0)`],
              }),
              motionTarget
            )
          );
        });

        this.cleanupScrollAnimations.push(
          FoxTheme.Motion.scroll(
            FoxTheme.Motion.animate(this, {
              clipPath: [
                `inset(0%)`,
                `inset(${Math.min(50, this.zoom)}px ${this.zoom}px round ${this.borderRadius}px)`,
              ],
            }),
            motionTarget
          )
        );

        if (this.sliderControls) {
          this.cleanupScrollAnimations.push(
            FoxTheme.Motion.scroll(
              FoxTheme.Motion.animate(this.sliderControls, {
                bottom: [`0`, `${Math.min(50, this.zoom)}px`],
              }),
              motionTarget
            )
          );
        }
      }

      destroyZoom() {
        this.cleanupScrollAnimations.forEach((fn) => fn());
        this.cleanupScrollAnimations = [];
        this.style.clipPath = 'none';
        if (this.sliderControls) this.sliderControls.style.bottom = 0;
      }
    }
  );
}
